import { useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import { LOGO_STYLES, LOGO_PALETTES, SAVED_LOGOS } from "../lib/sample-data";
import { Shapes, Wand2, Upload, Download, Save, Sparkles, Type } from "lucide-react";
import { cn } from "../lib/utils";

const FONT_OPTIONS = ["Outfit", "Playfair Display", "Space Grotesk", "Manrope", "IBM Plex Sans"];

export default function LogoLab() {
  const [style, setStyle] = useState("wordmark");
  const [palette, setPalette] = useState("aqua");
  const [font, setFont] = useState("Outfit");
  const [name, setName] = useState("PremierLab");

  const activePalette = LOGO_PALETTES.find((p) => p.id === palette);

  return (
    <div data-testid="logo-lab-page" className="space-y-8">
      <PageHeader
        kicker="LogoLab"
        title="Design your brand mark"
        description="Generate, upload, and refine logos. Save variants to BrandLab and export to PNG, SVG, or PDF."
        actions={
          <>
            <button
              data-testid="logo-upload-btn"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-[var(--pl-border)] text-sm text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
            >
              <Upload className="h-4 w-4" /> Upload Logo
            </button>
            <button
              data-testid="logo-generate-btn"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
            >
              <Wand2 className="h-4 w-4" /> Generate
            </button>
          </>
        }
      />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 p-6 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Preview</div>
          <div
            className="mt-3 rounded-2xl aspect-[16/9] grid place-items-center relative overflow-hidden"
            style={{ background: activePalette.hex[0] }}
            data-testid="logo-preview"
          >
            <div
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight"
              style={{
                color: activePalette.hex[1],
                fontFamily: font,
              }}
            >
              {style === "monogram" && name?.[0]?.toUpperCase()}
              {style !== "monogram" && name}
              {style === "icon" && <span style={{ color: activePalette.hex[2] }}> ●</span>}
            </div>
            <div
              className="absolute bottom-3 right-4 text-[10px] uppercase tracking-[0.22em]"
              style={{ color: activePalette.hex[2] }}
            >
              {style} · {font}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <BrandTile bg={activePalette.hex[0]} fg={activePalette.hex[1]} label="Dark" name={name} font={font} />
            <BrandTile bg={activePalette.hex[1]} fg={activePalette.hex[0]} label="Aqua" name={name} font={font} />
            <BrandTile bg="#FFFFFF" fg={activePalette.hex[0]} label="Light" name={name} font={font} />
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Brand name</div>
            <input
              data-testid="logo-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
              className="mt-2 w-full rounded-xl border border-[var(--pl-border)] px-3 h-11 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
            />
          </div>

          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Style</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {LOGO_STYLES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`logo-style-${s.id}`}
                  onClick={() => setStyle(s.id)}
                  className={cn(
                    "rounded-xl px-3 py-2.5 text-left border text-xs transition",
                    style === s.id
                      ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                      : "border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
                  )}
                >
                  <div className="font-medium">{s.label}</div>
                  <div className="text-[10px] text-[var(--pl-text-muted)] mt-0.5">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Palette</div>
            <div className="mt-2 space-y-1.5">
              {LOGO_PALETTES.map((p) => (
                <button
                  key={p.id}
                  data-testid={`logo-palette-${p.id}`}
                  onClick={() => setPalette(p.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-left transition",
                    palette === p.id
                      ? "bg-[var(--pl-aqua)]/10 border-[var(--pl-aqua)]/40"
                      : "border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/30"
                  )}
                >
                  <div className="flex gap-0.5">
                    {p.hex.map((h) => (
                      <span key={h} className="h-5 w-5 rounded-md border border-white/10" style={{ background: h }} />
                    ))}
                  </div>
                  <span className="text-sm text-[var(--pl-text)]">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Typography</div>
            <div className="mt-2 flex items-center gap-2">
              <Type className="h-4 w-4 text-[var(--pl-aqua)]" />
              <select
                data-testid="logo-font"
                value={font}
                onChange={(e) => setFont(e.target.value)}
                style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
                className="flex-1 rounded-xl border border-[var(--pl-border)] px-3 h-10 text-sm focus:outline-none focus:border-[var(--pl-aqua)]/50"
              >
                {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              data-testid="logo-save-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl pl-btn-aqua text-sm"
            >
              <Save className="h-4 w-4" /> Save to BrandLab
            </button>
            <button
              data-testid="logo-export-btn"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[var(--pl-border)] text-sm text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
            >
              <Download className="h-4 w-4" /> Export
            </button>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Saved Logos</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SAVED_LOGOS.map((l) => (
            <div
              key={l.id}
              data-testid={`saved-logo-${l.id}`}
              className="rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/70 pl-lift"
            >
              <div className="aspect-[4/3] bg-[var(--pl-bg-2)] overflow-hidden">
                <img src={l.thumb} alt={l.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-sm font-medium text-[var(--pl-text)] truncate">{l.name}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] flex items-center gap-1 mt-0.5">
                  <Sparkles className="h-3 w-3 text-[var(--pl-aqua)]" /> PNG · SVG · PDF
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function BrandTile({ bg, fg, label, name, font }) {
  return (
    <div
      className="rounded-xl aspect-[4/3] grid place-items-center relative overflow-hidden border border-white/5"
      style={{ background: bg }}
    >
      <div className="font-display text-xl font-semibold" style={{ color: fg, fontFamily: font }}>
        {name}
      </div>
      <div className="absolute top-1.5 left-2 text-[9px] uppercase tracking-[0.22em]" style={{ color: fg, opacity: 0.6 }}>
        {label}
      </div>
    </div>
  );
}
